import { Injectable, NgZone, Inject } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { AuthService } from "@auth0/auth0-angular";
import { BehaviorSubject, Observable } from "rxjs";
import { Router } from "@angular/router";
import { ConfigService } from "../config/config.service";
import { UserService } from "src/app/features/users/services/user.service";
import { DeviceDetectorService } from "ngx-device-detector";
import { firstValueFrom } from "rxjs";
import type { AuthorizationParams } from "@auth0/auth0-angular";
import type { IdToken } from "@auth0/auth0-angular";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private readonly isNative: boolean;
  private redirectCallbackHandled = false;
  private isSessionRecoveryInProgress = false;
  private authenticationChange: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public authenticationChange$: Observable<boolean>;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private userService: UserService,
    private configService: ConfigService,
    private deviceService: DeviceDetectorService,
    @Inject(AuthService) private auth0: AuthService
  ) {
    this.isNative = Capacitor.isNativePlatform();
    this.authenticationChange$ = this.authenticationChange.asObservable();
    this.auth0.isAuthenticated$.subscribe((isAuth) => {
      this.ngZone.run(() => this.authenticationChange.next(isAuth));
    });
  }

  public login(): void {
    this.auth0.loginWithRedirect().subscribe();
  }

  public logout(): void {
    this.auth0.logout({
      logoutParams: {
        returnTo: this.isNative ? undefined : window.location.origin,
      },
    });
    if (this.isNative) {
      this.router.navigate(["/landing-screen"]);
      location.reload();
    }
  }

  public async handleLogin(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const hasCallbackParams = params.has("code") && params.has("state");
    if (!hasCallbackParams) {
      this.router.navigate(["/home"]);
      return;
    }
    if (this.redirectCallbackHandled) {
      this.router.navigate(["/home"]);
      return;
    }
    this.redirectCallbackHandled = true;
    try {
      const result = await firstValueFrom(
        this.auth0.handleRedirectCallback()
      );
      this.clearCallbackUrl();
      await this.lastLoginInfoUpdate();
      if (result?.appState?.returnTo) {
        this.router.navigateByUrl(result.appState.returnTo);
      } else {
        this.router.navigate(["/home"]);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isInvalidState = errorMessage.includes("Invalid state");
      if (isInvalidState) {
        console.warn("Auth callback skipped (invalid or expired state).");
      } else {
        console.error("Auth redirect callback failed", error);
      }
      this.clearCallbackUrl();
      this.router.navigate(["/home"]);
    }
  }

  private clearCallbackUrl(): void {
    const path = window.location.pathname;
    if (typeof window.history.replaceState === "function") {
      window.history.replaceState(null, "", path);
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    return firstValueFrom(this.auth0.isAuthenticated$);
  }

  public async getAccessToken(): Promise<string | undefined> {
    try {
      return await firstValueFrom(this.auth0.getAccessTokenSilently());
    } catch (error) {
      console.error("Error getting access token", error);
      const authErrorCode =
        typeof (error as { error?: unknown })?.error === "string"
          ? ((error as { error?: string }).error as string)
          : typeof (error as { error?: { error?: unknown } })?.error?.error ===
              "string"
            ? ((error as { error?: { error?: string } }).error?.error as string)
            : undefined;
      console.log("authErrorCode: ", authErrorCode);
      const isSessionExpiredError =
        authErrorCode === "login_required" ||
        authErrorCode === "consent_required" ||
        authErrorCode === "interaction_required" ||
        authErrorCode === "invalid_grant" ||
        authErrorCode === "missing_refresh_token";
      if (isSessionExpiredError) {
        this.executeSessionRecovery();
      }
      return undefined;
    }
  }

  private executeSessionRecovery(): void {
    if (this.isSessionRecoveryInProgress) {
      return;
    }
    this.isSessionRecoveryInProgress = true;
    this.logout();
  }

  public async idTokenClaims(): Promise<IdToken | undefined> {
    const claims = await firstValueFrom(this.auth0.idTokenClaims$);
    return claims ?? undefined;
  }

  public loginWithRedirect(authorizationParams?: AuthorizationParams): void {
    this.auth0.loginWithRedirect({
      authorizationParams,
    }).subscribe();
  }

  public getDeviceInfo(): string {
    const deviceInfo = this.deviceService.getDeviceInfo();
    return `${deviceInfo.deviceType}(${deviceInfo.os})`;
  }

  public async lastLoginInfoUpdate(): Promise<void> {
    const data = await this.idTokenClaims();
    const deviceInformation = this.getDeviceInfo();
    this.userService
      .updateLastLogin(data?.email, deviceInformation)
      .subscribe(() => {});
  }
}